import type { UserLocalStorage } from '../../types/localStorage';

import { useCallback, useRef } from 'react';
import moment from 'moment/moment';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';

import { useAdditionalDataModal } from '../AdditionalDataModal/useAdditionalDataModal';
import { usePopulateSpaceModal } from '../PopulateSpaceModal/usePopulateSpaceModal';

export const useOnboardingModals = (testId: string) => {
  const { openAdditionalDataModal } = useAdditionalDataModal(testId);
  const { openPopulateSpaceModal } = usePopulateSpaceModal(testId);

  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');
  const modalOpened = useRef<boolean>(false);

  const compareNextOpenModalDate = useCallback((nextDate: string) => {
    const inputDate = moment(nextDate, 'YYYY-MM-DD');
    const today = moment();
    return today.isSameOrAfter(inputDate, 'day');
  }, []);

  const handleOnboardingModals = useCallback(async () => {
    if (!user || modalOpened.current) return;

    if (getEnv('VITE_SHOW_ONBOARDING_MODAL') === 'false') return;

    const additionalInformationStatusRequired = 'required';
    const additionalInformationStatusOptional = 'optional';

    const additionalInformationStatus = user.data.additionalInformationStatus;
    const openModalAt = user.data.additionalInformationModalOpenAt?.date;

    const isFounder = user.data.organization_name === user.data.username;
    const isFreePlan = user.data.limits_plan.price === 0;

    const isRequired =
      additionalInformationStatus === additionalInformationStatusRequired;

    const isOptional =
      additionalInformationStatus === additionalInformationStatusOptional &&
      (!openModalAt || compareNextOpenModalDate(openModalAt));

    modalOpened.current = true;

    if (isRequired && isFounder && !isFreePlan) {
      openPopulateSpaceModal();
    }

    if (isRequired || isOptional) {
      await openAdditionalDataModal(isOptional);
    }
  }, [
    compareNextOpenModalDate,
    openAdditionalDataModal,
    openPopulateSpaceModal,
    user,
  ]);

  return { handleOnboardingModals };
};
